import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import { useEffect, useRef } from 'react'

/**
 * Real rich-text block editor — ports LiangCollection's `content_editor.js`
 * `text` block handling (`static/js/content_editor.js:1021-1044`) 1:1:
 * same library (Quill 2.x, "snow" theme), same toolbar groups
 * (bold/italic/underline, H2/H3, blockquote, ordered/bullet list, link,
 * align, clean). Replaces the earlier round's minimal
 * `contentEditable`+`execCommand` box (SOURCE_AUDIT_V2.md §5 "Rich text"
 * row) — the "不允许只做 textarea" requirement.
 *
 * Emits sanitized-server-side HTML on every text-change (debounced by the
 * caller), matching `sanitize_rich_text`'s allowed-tag whitelist on the
 * backend as the real security boundary; this is authoring convenience
 * only.
 */
export default function RichTextEditor({ html, onChange }: { html: string; onChange: (html: string) => void }) {
  const hostRef = useRef<HTMLDivElement>(null)
  const quillRef = useRef<Quill | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  // Tracks the last HTML this editor itself emitted, so the sync effect
  // below can tell "external" content changes (undo/redo, reload) apart
  // from the editor's own keystrokes and avoid clobbering cursor position
  // while typing.
  const lastEmittedRef = useRef(html)

  useEffect(() => {
    if (!hostRef.current) return
    const quill = new Quill(hostRef.current, {
      theme: 'snow',
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{ header: 2 }, { header: 3 }],
          ['blockquote'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link'],
          [{ align: [] }],
          ['clean'],
        ],
      },
    })
    // UI_V2 round bug fix: raw `quill.root.innerHTML =` assignment during
    // init is a DOM mutation Quill's own MutationObserver picks up and
    // re-emits as a *live* 'text-change' — even though `quill.on(...)` is
    // wired up below this line, that observer callback is async (queued
    // via MutationObserver's microtask), so it still fires shortly after
    // mount and reaches `handler`. That phantom "user edit" (bogus content
    // going from '' -> the initial html) pushes an unwanted history entry
    // that then sits on top of the undo stack, silently shadowing the
    // next real action a user tries to Undo (repro: add module -> add two
    // blocks -> reorder -> Undo actually undoes this phantom edit, not the
    // reorder). Using Quill's own API with source 'silent' suppresses that
    // synthetic event entirely, matching the guard already used in the
    // "reflect external changes" effect below.
    const initialDelta = quill.clipboard.convert({ html: html || '' })
    quill.setContents(initialDelta, 'silent')
    lastEmittedRef.current = quill.root.innerHTML
    quillRef.current = quill
    const handler = () => {
      const next = quill.root.innerHTML
      lastEmittedRef.current = next
      onChangeRef.current(next)
    }
    quill.on('text-change', handler)
    return () => {
      quill.off('text-change', handler)
      quillRef.current = null
      // Quill's snow theme inserts the toolbar as a DOM sibling *before*
      // the host element (not a child of it), so React unmounting/clearing
      // the host div does not remove it — without this it leaks a phantom
      // toolbar on every unmount (block delete) and, in React 18
      // StrictMode dev double-invoke, duplicates the toolbar on mount.
      const toolbar = quill.getModule('toolbar') as { container?: HTMLElement | null } | null
      toolbar?.container?.parentNode?.removeChild(toolbar.container)
      if (hostRef.current) {
        hostRef.current.innerHTML = ''
        hostRef.current.className = 'ge-quill-host'
      }
    }
    // Intentionally only re-init on mount (per-block instance, same as
    // LiangCollection's `quillInstances[block.id]` map keyed per block id —
    // the caller renders one `RichTextEditor` per block, key={block.id}).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reflect content changes that happened outside this editor instance
  // (undo/redo jumping the block's stored HTML, or an autosave/reload
  // resetting it) into the live Quill DOM. Without this, undoing a text
  // edit updates the block's serialized content but leaves the open
  // editor showing the stale, pre-undo text.
  //
  // Must go through Quill's own API with source 'silent' (not raw
  // `root.innerHTML =`): Quill's MutationObserver treats *any* DOM
  // mutation as a live user edit and re-emits 'text-change', which would
  // otherwise bounce straight back into onChange → re-save → re-push a
  // history entry, silently wiping the very redo entry undo() just
  // produced (this is what breaks redo if hooked up naively).
  useEffect(() => {
    const quill = quillRef.current
    if (!quill) return
    const next = html || ''
    if (next === lastEmittedRef.current) return
    lastEmittedRef.current = next
    const delta = quill.clipboard.convert({ html: next })
    quill.setContents(delta, 'silent')
  }, [html])

  return <div className="ge-quill-host" ref={hostRef} />
}
